import * as ko from "knockout";
import { AuthMethod, AuthState } from './contracts';
import { LocalizationManager } from './localization';
import { getCookie, setCookie, removeCookie } from 'typescript-cookie';

class LocalStorageManager {

  public getItem<T>(id: string, defaultValue: T | null): T | null {
    try {
      const jsonValue = localStorage.getItem(id);
      if (jsonValue !== null) {
        return JSON.parse(jsonValue);
      }
    } catch {
      console.log(`Failed to get storage object for key ${id}`);
    }
    return defaultValue;
  }

  public removeItem(id: string): void {
    try {
      localStorage.removeItem(id);
    } catch {
      console.log(`Failed to remove local storage object for key ${id}`);
    }
  }

  public setItem<T>(id: string, value: T): void {
    try {
      const jsonValue = JSON.stringify(value);
      localStorage.setItem(id, jsonValue);
    } catch {
      console.log(`Failed to set local storage object ${value} for key ${id}`);
    }
  }
}

const LAST_LOGIN_KEY = "last-login-method";

class LastLoginMethodStore {
  private readonly mode: LastLoginStorage;

  constructor(mode: string) {
    this.mode = (Object.values(LastLoginStorage) as string[]).includes(mode)
      ? mode as LastLoginStorage
      : LastLoginStorage.Off;
  }

  getId(): string | null {
    if (this.mode === LastLoginStorage.Off) {
      return null;
    }

    try {
      if (this.mode === LastLoginStorage.Cookie) {
        return getCookie(LAST_LOGIN_KEY) ?? null;
      }

      // localStorage: legacy format stored full AuthMethod JSON, new format stores just the ID
      const raw = localStorage.getItem(LAST_LOGIN_KEY);
      if (raw === null) {
        return null;
      }
      try {
        const parsed = JSON.parse(raw);
        return typeof parsed === 'string' ? parsed : parsed?.id ?? null;
      } catch {
        return raw;
      }
    } catch {
      return null;
    }
  }

  set(method: AuthMethod): void {
    if (this.mode === LastLoginStorage.Off || method.id === null) {
      return;
    }

    try {
      if (this.mode === LastLoginStorage.Cookie) {
        setCookie(LAST_LOGIN_KEY, method.id, { path: '/', expires: 365, secure: true, sameSite: 'Strict' });
      } else {
        localStorage.setItem(LAST_LOGIN_KEY, method.id);
      }
    } catch {
      // storage unavailable
    }
  }

  remove(): void {
    if (this.mode === LastLoginStorage.Off) {
      return;
    }

    try {
      if (this.mode === LastLoginStorage.Cookie) {
        removeCookie(LAST_LOGIN_KEY, { path: '/' });
      } else {
        localStorage.removeItem(LAST_LOGIN_KEY);
      }
    } catch {
      // storage unavailable
    }
  }
}

class LogonmeConfiguration {

  public baseUrl: string;
  public tenant: string;
  public version: string;
  public startupMethod: string | null;
  public startupParams: string | null;
  public startupLanguage: string | null;

  constructor(currentUrl: string) {
    const url = new URL(currentUrl);
    this.baseUrl = url.port ? `${url.protocol}//${url.hostname}:${url.port}` : `${url.protocol}//${url.hostname}`;
    this.tenant = url.searchParams.get("tenant") ?? "test";
    this.version = url.searchParams.get("version") ?? "n/a";
    this.startupParams = url.searchParams.get("startupParams");
    this.startupMethod = url.searchParams.get("method");
    this.startupLanguage = url.searchParams.get("lang");
  }
}

enum ViewId {
  None,
  Methods,
  Auth,
  Home
}

class LogonmeGlobalData {

  public DebugLog: boolean;
  public LocalStorageManager: LocalStorageManager;
  public LocalizationManager: LocalizationManager;
  public Configuration: LogonmeConfiguration;
  public SelectedLanguage: ko.Observable<string>;
  public SelectedMethod: ko.Observable<AuthMethod | null>;
  public AuthState: ko.Observable<AuthState>;
  public ViewConstructedCount: ko.Observable<number>;
  public ActiveView: ko.Observable<ViewId>;
  public HasConsent: ko.Observable<boolean>;
  public FrontendConfig: FrontendConfiguration;
  public LastLoginStore: LastLoginMethodStore;

  constructor(localizationManager: LocalizationManager, currentUrl: string, frontendConfig: FrontendConfiguration) {
    this.LocalStorageManager = new LocalStorageManager();
    this.LocalizationManager = localizationManager;
    this.Configuration = new LogonmeConfiguration(currentUrl);
    this.FrontendConfig = frontendConfig;
    this.DebugLog = frontendConfig.enableDebugLogging;
    this.LastLoginStore = new LastLoginMethodStore(frontendConfig.lastLoginMethodStorage);
    this.SelectedLanguage = ko.observable<string>(this.Configuration.startupLanguage ?? "default");
    this.SelectedMethod = ko.observable<AuthMethod | null>(null);
    this.AuthState = ko.observable<AuthState>(AuthState.Idle);
    this.ViewConstructedCount = ko.observable<number>(0);
    this.ActiveView = ko.observable<ViewId>(ViewId.None);
    this.HasConsent = ko.observable<boolean>(false);

    const hasConsent = hasMatchingCookieConsent(this.Configuration.tenant) || this.FrontendConfig.suppressCookieConsentDialog;
    if (hasConsent) {
      this.HasConsent(true);
    }

    this.SelectedLanguage.subscribe((lang: string) => {
      this.dump(`SelectedLanguage changed ${lang}`);
    });

    this.SelectedMethod.subscribe((method: AuthMethod | null) => {
      this.dump(`SelectedMethod changed ${method?.id}`);
      if (this.AuthState() !== AuthState.PathComplete) {
        if (method === null) {
          this.ActiveView(ViewId.Methods);
        } else {
          this.ActiveView(ViewId.Auth);
        }
      }
    });

    this.AuthState.subscribe((authState: AuthState) => {
      this.dump(`AuthState changed ${authState}`);

      switch (authState) {
        case AuthState.PathComplete:
          this.ActiveView(ViewId.Home);
          break;

        case AuthState.PathPartialComplete:
        case AuthState.PathCancelled:
        case AuthState.LogoutCompleted:
          this.ActiveView(ViewId.Methods);
          break;

        case AuthState.PathFailed:
          break;
      }
    });

    function hasMatchingCookieConsent(tenant: string): boolean {
      const consentTenant: string = getCookie(".logonme-cookie-consent") ?? "";
      return tenant.localeCompare(consentTenant, undefined, { sensitivity: 'accent' }) === 0;
    }
  }

  log(...args: unknown[]): void {
    if (!this.DebugLog) {
      return;
    }
    console.log(...args);
  }

  dump(onChange: string): void {
    if (!this.DebugLog) {
      return;
    }
    console.log(onChange);
    console.log(` => SelectedLanguage ${this.SelectedLanguage()}`);
    console.log(` => SelectedMethod ${this.SelectedMethod()?.id}`);
    console.log(` => ActiveView ${this.ActiveView()}`);
    console.log(` => AuthState ${this.AuthState()}`);
  }
}

enum LastLoginStorage {
  Off = "off",
  LocalStorage = "localStorage",
  Cookie = "cookie"
}

interface FrontendConfiguration {
  showDirectoryInfo: boolean;
  suppressCookieConsentDialog: boolean;
  enableDebugLogging: boolean;
  lastLoginMethodStorage: string;
  autoRedirectSingleApplication: boolean;
  allowedRedirectTargets: string[];
  allowedScriptSources: string[];
}

function isAllowedUrl(url: string, allowedTargets: string[]): boolean {
  try {
    // Relative URLs are same-origin — always allowed
    if (url.startsWith('/')) {
      return true;
    }
    // Wildcard allows all redirects
    if (allowedTargets.includes('*')) {
      return true;
    }
    return allowedTargets.some(target =>
      url.toLowerCase().startsWith(target.toLowerCase())
    );
  } catch {
    return false;
  }
}

function isAllowedScriptUrl(url: string, allowedSources: string[]): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') {
      return false;
    }
    return allowedSources.some(source => {
      const sourceUrl = source.startsWith('https://') ? source : `https://${source}`;
      return url.toLowerCase().startsWith(sourceUrl.toLowerCase());
    });
  } catch {
    return false;
  }
}

export { LogonmeConfiguration, LogonmeGlobalData, ViewId, FrontendConfiguration, isAllowedUrl, isAllowedScriptUrl };
